import { Api } from '@monorepo/shared/apiClient';
import {
  LogEntryCacheEvents,
  logEntryCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/logEntryCacheRegister';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type KeyParams = Api.FetchEdpAiSubmissionParams;

const KEY_START = 'edpAiSubmission';
export const createQueryKey = ({
  organizationId,
  logId,
  projectId,
  edpAiSubmissionId,
}: KeyParams) =>
  [KEY_START, organizationId, logId, projectId, edpAiSubmissionId] as const;

type Fetcher = Api.DataHookQueryFn<
  typeof createQueryKey,
  typeof Api.fetchEdpAiSubmission
>;

type UseEdpAiSubmissionParams = Partial<Api.FetchEdpAiSubmissionParams> & {
  config?: QueryConfig<Api.FetchEdpAiSubmissionResponse, Api.ErrorResponse>;
};

const fetcher: Fetcher = (
  _: string,
  organizationId: string,
  logId: string,
  projectId: string,
  edpAiSubmissionId: string,
) =>
  Api.fetchEdpAiSubmission({
    organizationId,
    projectId,
    logId,
    edpAiSubmissionId,
  });

export const useEdpAiSubmission = ({
  config: inputConfig,
  organizationId,
  logId,
  projectId,
  edpAiSubmissionId,
}: UseEdpAiSubmissionParams) => {
  const isEnabled =
    !!organizationId && !!projectId && !!logId && !!edpAiSubmissionId;
  const config = getQueryConfig<Api.FetchEdpAiSubmissionResponse>(
    inputConfig,
    isEnabled,
  );
  const key = isEnabled
    ? createQueryKey({ organizationId, logId, projectId, edpAiSubmissionId })
    : undefined;
  const { data: edpAiSubmission, ...queryInfo } = useQuery(
    key,
    fetcher,
    config,
  );
  return {
    edpAiSubmission,
    ...queryInfo,
  };
};

type InvalidateCacheParams = Omit<
  KeyParams,
  'projectId' | 'edpAiSubmissionId' | 'logId'
> & {
  edpAiSubmissionId?: string;
  logId?: string;
  projectId?: string;
};
async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([
      KEY_START,
      keyParams.organizationId,
      keyParams.logId,
      keyParams.projectId,
      keyParams.edpAiSubmissionId,
    ]),
  );
}

const cacheRegisterInvalidator = {
  hookName: 'useEdpAiSubmission',
  callback: (keyParams: InvalidateCacheParams) => invalidateCache(keyParams),
};

logEntryCacheRegister(
  [
    LogEntryCacheEvents.CREATE,
    LogEntryCacheEvents.UPDATE,
    LogEntryCacheEvents.DELETE,
  ],
  cacheRegisterInvalidator,
);
